import React, { useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore, BoardSpace } from '../store/gameStore';
import PlayerToken from './PlayerToken';
import albumCover from '../assets/album-cover.png';

const VIDEO_MAP: Record<string, string> = {
  'the-streets': '/dlh8967e0f.mp4',
  'hustle-hard': '/axign287ii.mp4',
  'side-quest': '/GrindModeBGvideo.mp4',
  'grind-mode': '/SideQuestBGvideo.mp4',
  'close-call': '/1CloseCallBGvideo.mp4',
  'stackin-paper': '/StackingPaperBGvideo.mp4',
  'the-come-up': '/LoyalFriendsBGvideo.mp4',
  'loyal-friends': '/TheComeUpBGvideo.mp4',
  'betrayal': '/BetrayalBGvideo.mp4',
  'breaking-free': '/BreakingFreeBGvideo.mp4',
  'the-deal': '/CloseCall2BGvideo.mp4',
  'close-call-2': '/TheDealBGvideo.mp4',
  'almost-there': '/FinalStretchBGvideo.mp4',
  'final-stretch': '/AlmostThereBGvideo.mp4',
  'one-more-step': '/OneMoreStepBGvideo.mp4',
  'victory-lap': '/VictoryLapBGvideo.mp4',
  'fame-fortune': '/FameFortuneBGvideo.mp4',
  'back-to-prison': '/Back2PrisonBGvideo.mp4',
};

const GameBoard: React.FC = () => {
  const { boardSpaces, players, currentPlayerIndex, playingVideo, setPlayingVideo, pendingWin, setPendingWin, nextTurn } = useGameStore();
  const videoRef = useRef<HTMLVideoElement>(null);

  // Auto-play video when playingVideo changes
  useEffect(() => {
    if (playingVideo && videoRef.current) {
      // Reset video to beginning and play
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  }, [playingVideo]);

  // Handle video end - clear video and proceed to next turn (or trigger win/prison/charades continue)
  const handleVideoEnd = useCallback(() => {
    const state = useGameStore.getState();
    const currentPendingWin = state.pendingWin;
    const currentPendingPrison = state.pendingPrison;
    const isCharadesWaiting = state.charadesWaitingForVideo;
    
    // Handle Back to Prison - send player to prison after video ends
    if (currentPendingPrison) {
      const currentActivePlayer = state.players[state.currentPlayerIndex];
      if (currentActivePlayer) {
        state.sendToPrison(currentActivePlayer.id);
      }
      useGameStore.setState({ pendingPrison: false });
    }
    
    setPlayingVideo(null);
    if (currentPendingWin) {
      setPendingWin(false);
      setTimeout(() => {
        useGameStore.setState({ phase: 'finished', showMoneyConfetti: true });
      }, 300);
    } else if (isCharadesWaiting) {
      // Continue charades rolling flow after video ends
      setTimeout(() => {
        state.charadesContinueAfterVideo();
      }, 300);
    } else {
      // Delay nextTurn slightly to allow album cover to fade in
      setTimeout(() => {
        nextTurn();
      }, 300);
    }
  }, [setPlayingVideo, nextTurn, setPendingWin]);

  const totalSpaces = boardSpaces.length;
  const boardRadius = 380; // Radius of the circular board
  const boardSize = 900; // Total board area size for scrollable content

  // Calculate position for each space on the circle
  const getPosition = (index: number) => {
    // Start from bottom (6 o'clock position) and go clockwise
    const angle = (index / totalSpaces) * Math.PI * 2 - Math.PI / 2;
    const x = Math.cos(angle) * boardRadius;
    const y = Math.sin(angle) * boardRadius;
    return { x, y, angle: (angle * 180) / Math.PI };
  };

  // Get players at a specific position
  const getPlayersAtPosition = (position: number) => {
    return players.filter((p) => p.position === position);
  };

  const getSpaceColor = (space: BoardSpace) => {
    switch (space.type) {
      case 'start':
      case 'prison':
        return 'from-red-500 to-red-700';
      case 'trivia':
        return 'from-purple-500 to-purple-700';
      case 'game':
        return 'from-amber-500 to-orange-600';
      case 'fame':
        return 'from-yellow-400 to-yellow-600';
      default:
        return 'from-gray-600 to-gray-800';
    }
  };

  const getSpaceEmoji = (space: BoardSpace) => {
    switch (space.type) {
      case 'start':
      case 'prison':
        return '🔒';
      case 'trivia':
        return '❓';
      case 'game':
        return '🎮';
      case 'fame':
        return '👑';
      default:
        return '📍';
    }
  };

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      {/* Scrollable board container */}
      <div
        className="relative"
        style={{ width: boardSize, height: boardSize, minWidth: boardSize, minHeight: boardSize, marginLeft: '-5%' }}
      >
        {/* Connection lines between spaces */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox={`-${boardSize/2} -${boardSize/2} ${boardSize} ${boardSize}`}>
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#d97706" stopOpacity="0.5" />
              <stop offset="50%" stopColor="#f59e0b" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#d97706" stopOpacity="0.5" />
            </linearGradient>
          </defs>
          {boardSpaces.map((_, index) => {
            const current = getPosition(index);
            const next = getPosition((index + 1) % totalSpaces);
            return (
              <line
                key={`line-${index}`}
                x1={current.x}
                y1={current.y}
                x2={next.x}
                y2={next.y}
                stroke="url(#lineGradient)"
                strokeWidth="2"
                strokeDasharray={index % 5 === 0 ? "none" : "4,4"}
              />
            );
          })}
        </svg>

        {/* Board spaces */}
        {boardSpaces.map((space, index) => {
          const pos = getPosition(index);
          const playersHere = getPlayersAtPosition(index);

          return (
            <motion.div
              key={space.id}
              className="absolute"
              style={{
                left: `calc(50% + ${pos.x}px)`,
                top: `calc(50% + ${pos.y}px)`,
                transform: 'translate(-50%, -50%)',
              }}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.03, type: 'spring' }}
            >
              {/* Space tile */}
              <motion.div
                className={`
                  relative w-[72px] h-[72px] rounded-xl
                  bg-gradient-to-br ${getSpaceColor(space)}
                  flex flex-col items-center justify-center
                  border-2 ${space.type === 'fame' ? 'border-yellow-300 shadow-lg shadow-yellow-500/50' : 'border-white/30'}
                  cursor-pointer
                  shadow-lg
                `}
                whileHover={{ scale: 1.15, zIndex: 50 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <span className="text-xl">{getSpaceEmoji(space)}</span>
                <span className="text-[8px] text-white font-bold uppercase tracking-wider mt-0.5 text-center leading-tight px-0.5">
                  {space.label}
                </span>
                {/* Space number */}
                <div className="absolute -top-2 -right-2 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center">
                  <span className="text-[9px] text-white font-bold">{index}</span>
                </div>
              </motion.div>

              {/* Player tokens on this space */}
              {playersHere.length > 0 && (
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 flex gap-1">
                  {playersHere.map((player) => (
                    <PlayerToken
                      key={player.id}
                      player={player}
                      isActive={players[currentPlayerIndex]?.id === player.id}
                      size="sm"
                    />
                  ))}
                </div>
              )}
            </motion.div>
          );
        })}

        {/* Center album cover / video */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
          <motion.div
            className="relative"
            animate={{
              boxShadow: playingVideo
                ? ['0 0 30px rgba(255, 0, 100, 0.4)', '0 0 60px rgba(255, 0, 100, 0.7)', '0 0 30px rgba(255, 0, 100, 0.4)']
                : [
                    '0 0 20px rgba(0, 200, 255, 0.2)',
                    '0 0 35px rgba(0, 200, 255, 0.4)',
                    '0 0 20px rgba(0, 200, 255, 0.2)',
                  ],
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="relative w-80 h-80 rounded-2xl overflow-hidden border-4 border-cyan-400">
              <AnimatePresence mode="wait">
                {playingVideo ? (
                  <motion.div
                    key={`video-${playingVideo}`}
                    className="w-full h-full"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.5 }}
                  >
                    <video
                      ref={videoRef}
                      src={VIDEO_MAP[playingVideo]}
                      className="w-full h-full object-cover"
                      onEnded={handleVideoEnd}
                      muted
                      autoPlay
                      playsInline
                      preload="auto"
                    />
                  </motion.div>
                ) : (
                  <motion.img
                    key="album-cover"
                    src={albumCover}
                    alt="I'm Gon't Be Rich If I Don't Die - The Mack Rep"
                    className="w-full h-full object-cover"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.5 }}
                  />
                )}
              </AnimatePresence>

              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
            </div>

            {/* Album title / Video label */}
            <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap">
              <div className="bg-black/80 px-4 py-2 rounded-full border border-cyan-400/50">
                {playingVideo ? (
                  <p className="text-red-400 font-bold text-xs tracking-wider animate-pulse">
                    {playingVideo === 'the-streets' ? '🎵 THE STREETS' : playingVideo === 'hustle-hard' ? '🎵 HUSTLE HARD' : playingVideo === 'side-quest' ? '🎵 SIDE QUEST' : playingVideo === 'grind-mode' ? '🎵 GRIND MODE' : playingVideo === 'close-call' ? '🎵 CLOSE CALL' : playingVideo === 'stackin-paper' ? '🎵 STACKIN\' PAPER' : playingVideo === 'the-come-up' ? '🎵 THE COME UP' : playingVideo === 'loyal-friends' ? '🎵 LOYAL FRIENDS' : playingVideo === 'betrayal' ? '🎵 BETRAYAL' : playingVideo === 'breaking-free' ? '🎵 BREAKING FREE' : playingVideo === 'the-deal' ? '🎵 THE DEAL' : playingVideo === 'close-call-2' ? '🎵 CLOSE CALL 2' : playingVideo === 'almost-there' ? '🎵 ALMOST THERE' : playingVideo === 'final-stretch' ? '🎵 FINAL STRETCH' : playingVideo === 'one-more-step' ? '🎵 ONE MORE STEP' : playingVideo === 'victory-lap' ? '🎵 VICTORY LAP' : playingVideo === 'back-to-prison' ? '🔒 BACK TO PRISON' : '🎵 FAME & FORTUNE'}
                  </p>
                ) : (
                  <p className="text-cyan-400 font-bold text-xs tracking-wider">THE MACK REP</p>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default GameBoard;